import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
// Components
import FullButton from "../Buttons/FullButton";
import Footer from "./Footer";
import TopNavbar from "../Nav/TopNavbar";
import PdfIcon from "../Elements/PdfIcon";
import { getProductBySlug } from "../../data/products";

const SITE_URL = "https://finechek.com";

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  if (!product) {
    return (
      <NotFoundWrapper className="flexCenter" style={{ flexDirection: "column" }}>
        <h1 className="font40 extraBold">Product not found</h1>
        <RouterLink to="/">
          <FullButton title="Back to Home" />
        </RouterLink>
      </NotFoundWrapper>
    );
  }

  const pageTitle = `${product.name} — ${product.tagline} | Finechek`;
  const canonicalUrl = `${SITE_URL}/products/${product.slug}`;
  const imageUrl = `${SITE_URL}${product.img}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: imageUrl,
    brand: {
      "@type": "Brand",
      name: "Finechek",
    },
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={product.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <TopNavbar />

      <div className="whiteBg">
        <div className="container">
          <Hero className="flexSpaceCenter">
            <HeroImgWrapper className="flexCenter">
              <img className="radius8" src={product.img} alt={product.alt} />
            </HeroImgWrapper>
            <HeroText>
              <Tag className="font15 semiBold purpleColor">{product.tagline}</Tag>
              <h1 className="font40 extraBold">{product.name}</h1>
              <p className="font15">{product.description}</p>
              <ButtonsRow className="flexNullCenter">
                <a
                  href={product.brochureHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FullButton title={product.brochureLabel} icon={<PdfIcon />} />
                </a>
                <div style={{ width: "190px", marginLeft: "15px" }}>
                  <RouterLink to="/contact">
                    <FullButton title="Contact Us" border />
                  </RouterLink>
                </div>
              </ButtonsRow>
            </HeroText>
          </Hero>
        </div>
      </div>

      {product.keyFeatures?.length > 0 && (
        <div className="lightBg">
          <div className="container">
            <SectionTitle className="font30 extraBold">Key Features</SectionTitle>
            <FeaturesGrid>
              {product.keyFeatures.map((feature) => (
                <FeatureCard className="radius8 whiteBg" key={feature.title}>
                  <h3 className="font20 extraBold">{feature.title}</h3>
                  <p className="font10">{feature.text}</p>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </div>
        </div>
      )}

      {product.specs?.length > 0 && (
        <div className="whiteBg">
          <div className="container">
            <SectionTitle className="font30 extraBold">Specifications</SectionTitle>
            <SpecsTable className="radius8">
              {product.specs.map((spec) => (
                <SpecRow key={spec.label}>
                  <SpecLabel className="semiBold">{spec.label}</SpecLabel>
                  <SpecValue>{spec.value}</SpecValue>
                </SpecRow>
              ))}
            </SpecsTable>
          </div>
        </div>
      )}

      <Footer />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
`;
const NotFoundWrapper = styled.div`
  min-height: 60vh;
  gap: 20px;
`;
const Hero = styled.div`
  padding: 140px 0 60px 0;
  gap: 60px;
  @media (max-width: 860px) {
    flex-direction: column;
    padding: 100px 0 30px 0;
  }
`;
const HeroImgWrapper = styled.div`
  width: 40%;
  background-color: #f9f7fa;
  padding: 40px;
  img {
    max-width: 100%;
    height: auto;
  }
  @media (max-width: 860px) {
    width: 100%;
  }
`;
const HeroText = styled.div`
  width: 60%;
  @media (max-width: 860px) {
    width: 100%;
    margin-top: 30px;
  }
`;
const Tag = styled.div`
  margin-bottom: 10px;
`;
const ButtonsRow = styled.div`
  margin: 30px 0;
  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
    div {
      margin: 15px 0 0 0 !important;
    }
  }
`;
const SectionTitle = styled.h2`
  padding: 60px 0 20px 0;
  @media (max-width: 860px) {
    text-align: center;
    padding: 40px 0 20px 0;
  }
`;
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  padding-bottom: 60px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;
const FeatureCard = styled.div`
  padding: 25px;
  box-shadow: 0 4px 20px rgba(11, 9, 59, 0.08);
`;
const SpecsTable = styled.div`
  border: 1px solid #eee;
  margin-bottom: 60px;
  overflow: hidden;
`;
const SpecRow = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  :last-child {
    border-bottom: none;
  }
  :nth-child(odd) {
    background-color: #f9f7fa;
  }
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;
const SpecLabel = styled.div`
  width: 35%;
  padding: 14px 20px;
  color: #0b093b;
  @media (max-width: 600px) {
    width: 100%;
    padding-bottom: 4px;
  }
`;
const SpecValue = styled.div`
  width: 65%;
  padding: 14px 20px;
  color: #707070;
  @media (max-width: 600px) {
    width: 100%;
    padding-top: 0;
  }
`;
